/**
 * The HTTP client and its authentication machinery.
 *
 * The token model, and why it is shaped this way:
 *
 * - The **access token lives in memory only** — a ref inside AuthContext,
 *   never localStorage and never a cookie JavaScript can read. KDOSHR holds
 *   personnel records, and localStorage is readable by any injected script.
 *   The cost is that a page refresh loses it, which is exactly what the
 *   refresh flow below is for.
 * - The **refresh token is an HttpOnly cookie** scoped to `/api/v1/auth/`, so
 *   this code cannot read it and neither can an attacker's script.
 * - **CSRF** comes from the `csrftoken` cookie and goes back as `X-CSRFToken`
 *   on every non-GET, because the refresh cookie makes us cookie-authenticated
 *   for that one endpoint.
 *
 * `setAuthHelpers` is dependency injection rather than an import: this module
 * must not import React state, or the import graph becomes circular and the
 * client stops being testable on its own.
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { env } from '@/config/env';

import { normalizeError } from './errors';

/** Refresh this many seconds before the token actually expires. */
const REFRESH_LEEWAY_SECONDS = 30;

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_URL || '/',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
  // Required: the refresh token is a cookie and must ride along.
  withCredentials: true,
});

// --- injected by AuthContext at startup -----------------------------------

type GetToken = () => string | null;
type OnUnauthorized = () => void;
type OnTokenRefreshed = (token: string) => void;

let getAccessToken: GetToken = () => null;
let onUnauthorized: OnUnauthorized = () => undefined;
let onTokenRefreshed: OnTokenRefreshed = () => undefined;

export function setAuthHelpers(handlers: {
  getToken: GetToken;
  onUnauthorized: OnUnauthorized;
  onTokenRefreshed: OnTokenRefreshed;
}): void {
  getAccessToken = handlers.getToken;
  onUnauthorized = handlers.onUnauthorized;
  onTokenRefreshed = handlers.onTokenRefreshed;
}

// --- helpers ---------------------------------------------------------------

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match?.[2] ? decodeURIComponent(match[2]) : null;
}

/** Auth endpoints must never trigger a refresh: that is how you get a loop. */
function isAuthEndpoint(url = ''): boolean {
  return (
    url.includes('/auth/login/') || url.includes('/auth/refresh/') || url.includes('/auth/logout/')
  );
}

function isExpiringSoon(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    if (typeof exp !== 'number') return false;
    return exp - Date.now() / 1000 < REFRESH_LEEWAY_SECONDS;
  } catch {
    // An unparseable token is not worth crashing over; let the 401 path handle it.
    return false;
  }
}

// --- single-flight refresh -------------------------------------------------
//
// Without this, N requests firing while the token is stale each start their own
// refresh. The guide project this was ported from recorded the failure mode: a
// backgrounded tab has its timers throttled, so on return every queued request
// woke at once and produced 90 401s in 440ms, each rotating the refresh token
// and invalidating the one the others were about to use.
//
// One promise, shared. Everyone else waits on it.

let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  refreshPromise ??= (async () => {
    try {
      const response = await axios.post<{ access: string }>(
        `${env.VITE_API_URL || ''}/api/v1/auth/refresh/`,
        {},
        { withCredentials: true, timeout: 15_000 },
      );
      const token = response.data.access;
      onTokenRefreshed(token);
      return token;
    } finally {
      // Cleared in `finally` so a failed refresh does not wedge every
      // subsequent request behind a permanently rejected promise.
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// --- request interceptor ---------------------------------------------------

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // A correlation id generated here means a user's screenshot maps to a
  // CloudWatch log line without anyone matching timestamps by hand. The API
  // echoes whatever we send.
  config.headers.set('X-Correlation-ID', crypto.randomUUID());

  const method = (config.method ?? 'get').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const csrf = readCookie('csrftoken');
    if (csrf) config.headers.set('X-CSRFToken', csrf);
  }

  if (isAuthEndpoint(config.url)) return config;

  let token = getAccessToken();

  // Pre-flight refresh: renew *before* sending rather than after a 401. Saves
  // a round trip and, more importantly, avoids the stampede described above.
  if (token && isExpiringSoon(token)) {
    try {
      token = await refreshAccessToken();
    } catch {
      // Fall through and let the response interceptor deal with the 401.
    }
  }

  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

// --- response interceptor --------------------------------------------------

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;

    const shouldRetry =
      error.response?.status === 401 &&
      config !== undefined &&
      !config._retried &&
      !isAuthEndpoint(config.url);

    if (shouldRetry) {
      config._retried = true;
      try {
        const token = await refreshAccessToken();
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
        return await apiClient.request(config);
      } catch {
        // The refresh token is gone or rejected: the session is genuinely over.
        onUnauthorized();
      }
    }

    // Everything downstream sees one error shape, never a raw AxiosError.
    return Promise.reject(normalizeError(error));
  },
);
